/**
 * TPG Platform — Program Scheduler contracts (Sprint 9 Phase A).
 *
 * Schedule authority lives on the TPG Platform. TPG Player receives a resolved
 * Media Asset (+ advertising plan) via channels/now → control plane.
 */

import { createMediaAsset, MediaPresentationKind } from "./MediaAsset";
import {
  ChannelKind,
  createChannel,
  mediaAssetFromChannel,
  resolveChannelPhase,
} from "./Channel";

export var ScheduleEntryKind = Object.freeze({
  PROGRAM: "program",
  BLOCK: "block",
  BREAK: "break",
  LIVE_EVENT: "live_event",
  FILLER: "filler",
});

export var ScheduleRecurrence = Object.freeze({
  ONCE: "once",
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  CUSTOM: "custom",
});

export var LiveEventState = Object.freeze({
  UPCOMING: "upcoming",
  COUNTDOWN: "countdown",
  LIVE: "live",
  INTERMISSION: "intermission",
  EXTENDED: "extended",
  ENDED: "ended",
  REPLAY: "replay",
});

export var AdvertisingPolicy = Object.freeze({
  NO_ADS: "no_ads",
  MEDIA_ASSET_ONLY: "media_asset_only",
  BROADCAST_ONLY: "broadcast_only",
  HYBRID: "hybrid",
  SPONSORED: "sponsored",
});

export var BreakAssetKind = Object.freeze({
  COMMERCIAL_BREAK: "commercial_break",
  SPONSOR_BREAK: "sponsor_break",
  STATION_ID: "station_id",
  PROMO: "promo",
  INTERMISSION: "intermission",
  COUNTDOWN: "countdown",
  HOUSE_AD: "house_ad",
  AUDIO_ANNOUNCEMENT: "audio_announcement",
  STATIC_SLIDE: "static_slide",
});

export var AutomationRuleType = Object.freeze({
  LOOP_PLAYLIST: "loop_playlist",
  REPEAT_DAILY: "repeat_daily",
  REPEAT_WEEKLY: "repeat_weekly",
  FILL_EMPTY: "fill_empty",
  EXTEND_LIVE: "extend_live",
  RESUME_SCHEDULE: "resume_schedule",
  FAILOVER: "failover",
  END_OF_DAY: "end_of_day",
});

/**
 * Normalize Platform channels/now payload into player-ready control inputs.
 * @param {object} payload
 * @returns {object}
 */
export function normalizeChannelNowPayload(payload) {
  payload = payload || {};
  var channel = createChannel({
    id: (payload.channel && payload.channel.unique_id) || null,
    kind: (payload.channel && payload.channel.kind) || ChannelKind.LINEAR,
    title: (payload.channel && payload.channel.name) || "",
    loopMode: (payload.channel && payload.channel.loop_mode) || null,
    currentProgram: payload.current
      ? {
          id: payload.current.unique_id,
          title: payload.current.title,
          startAt: payload.current.starts_at,
          endAt: payload.current.ends_at,
          asset: payload.media_asset || null,
          elapsedSeconds:
            payload.current.elapsed_seconds != null
              ? Number(payload.current.elapsed_seconds)
              : null,
        }
      : null,
    nextProgram: payload.next
      ? {
          id: payload.next.unique_id,
          title: payload.next.title,
          startAt: payload.next.starts_at,
          endAt: payload.next.ends_at,
        }
      : null,
    slate: payload.slate && payload.slate.image ? payload.slate.image : null,
  });

  var mediaAsset = payload.media_asset
    ? createMediaAsset(payload.media_asset)
    : mediaAssetFromChannel(channel);

  if (!mediaAsset.presentationKind || mediaAsset.presentationKind === "unknown") {
    mediaAsset.presentationKind = MediaPresentationKind.CHANNEL;
  }

  return {
    channel: channel,
    phase: payload.phase || resolveChannelPhase(channel),
    mediaAsset: mediaAsset,
    advertising: payload.advertising || {
      policy: AdvertisingPolicy.HYBRID,
      use_media_asset_avod: false,
      use_broadcast_breaks: false,
      avoid_duplicate_ads: true,
    },
    now: payload.now || null,
    // Preserve schedule join-NOW fields for continuous channel handoff.
    elapsedSeconds:
      payload.current && payload.current.elapsed_seconds != null
        ? Number(payload.current.elapsed_seconds)
        : null,
    remainingSeconds:
      payload.current && payload.current.remaining_seconds != null
        ? Number(payload.current.remaining_seconds)
        : payload.remaining_seconds != null
          ? Number(payload.remaining_seconds)
          : null,
    loopMode:
      (payload.channel && payload.channel.loop_mode) ||
      (channel && channel.loopMode) ||
      null,
    current: payload.current || null,
  };
}

/**
 * Decide whether AVOD plugin should run for a tuned channel.
 * @param {object} advertising  from normalizeChannelNowPayload
 * @param {object[]} [mediaAdCues]
 * @returns {{ enableAvod: boolean, preferMediaCues: boolean, enableBroadcastBreaks: boolean }}
 */
export function resolveChannelAvodMode(advertising, mediaAdCues) {
  advertising = advertising || {};
  var hasMediaCues = Array.isArray(mediaAdCues) && mediaAdCues.length > 0;
  var preferMedia = !!advertising.use_media_asset_avod && hasMediaCues;
  var broadcast = !!advertising.use_broadcast_breaks;
  if (advertising.avoid_duplicate_ads && preferMedia) {
    broadcast = false;
  }
  return {
    enableAvod: preferMedia || broadcast,
    preferMediaCues: preferMedia,
    enableBroadcastBreaks: broadcast,
  };
}

export {
  ChannelKind,
  createChannel,
  mediaAssetFromChannel,
  resolveChannelPhase,
};

export default {
  ScheduleEntryKind: ScheduleEntryKind,
  ScheduleRecurrence: ScheduleRecurrence,
  LiveEventState: LiveEventState,
  AdvertisingPolicy: AdvertisingPolicy,
  BreakAssetKind: BreakAssetKind,
  AutomationRuleType: AutomationRuleType,
  normalizeChannelNowPayload: normalizeChannelNowPayload,
  resolveChannelAvodMode: resolveChannelAvodMode,
};
